/**
 * Creature Library Service
 *
 * TASK-CLS-3.4: Frontend service for creature storage API
 *
 * Handles all creature library operations:
 * - Save creatures from generation results
 * - List creatures by owner
 * - Get individual creatures
 * - Batch fetch creatures
 */

import type {
  SerializedCreatureDocument,
  CreatureListResponse,
  BatchCreaturesResponse
} from '@shared/types/creature-storage';

// Use relative URL - Vite dev server will proxy to backend
const LIBRARY_API = '/api/creature-library';

import { getAuthToken as _getAuthToken } from './auth-token';

/**
 * Get auth token from auth service
 */
async function getAuthToken(): Promise<string> {
  return _getAuthToken();
}

/**
 * Save creature request
 */
export interface SaveCreatureRequest {
  jobId: string;
}

/**
 * Save creature response
 */
export interface SaveCreatureResponse {
  success: boolean;
  creatureId: string;
  creature: SerializedCreatureDocument;
}

/**
 * List creatures request
 */
export interface ListCreaturesRequest {
  limit?: number;
  offset?: number;
}

/**
 * Creature Library Service
 */
export class CreatureLibraryService {
  /**
   * Check if a generation job has already been saved to the gallery
   */
  async checkJobSaved(jobId: string): Promise<{ saved: boolean; creatureId?: string }> {
    try {
      const token = await getAuthToken();

      const response = await fetch(`${LIBRARY_API}/check-saved/${jobId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('[CreatureLibrary] Error checking job save status:', error);
      throw error;
    }
  }

  /**
   * Save a creature from a completed generation job
   * Uses authenticated user's ID as owner
   */
  async saveCreature(request: SaveCreatureRequest): Promise<SaveCreatureResponse> {
    const token = await getAuthToken();

    const response = await fetch(`${LIBRARY_API}/save`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ jobId: request.jobId })
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(error.error || `Failed to save creature: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * List creatures for authenticated user (includes shared pool)
   */
  async listCreatures(request: ListCreaturesRequest = {}): Promise<CreatureListResponse> {
    const token = await getAuthToken();

    const params = new URLSearchParams();
    if (request.limit !== undefined) {
      params.set('limit', request.limit.toString());
    }
    if (request.offset !== undefined) {
      params.set('offset', request.offset.toString());
    }

    const url = params.toString()
      ? `${LIBRARY_API}/list?${params}`
      : `${LIBRARY_API}/list`;

    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(error.error || `Failed to list creatures: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Get a single creature by ID
   */
  async getCreature(id: string): Promise<SerializedCreatureDocument> {
    const token = await getAuthToken();

    const response = await fetch(`${LIBRARY_API}/${id}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (response.status === 404) {
      throw new Error('Creature not found');
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(error.error || `Failed to get creature: ${response.statusText}`);
    }

    const data = await response.json();
    return data.creature;
  }

  /**
   * Update a creature's name
   * Requires ownership of the creature
   */
  async updateCreatureName(id: string, name: string): Promise<SerializedCreatureDocument> {
    const token = await getAuthToken();

    const response = await fetch(`${LIBRARY_API}/${id}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ name })
    });

    if (response.status === 404) {
      throw new Error('Creature not found');
    }

    if (response.status === 403) {
      throw new Error('You can only rename your own creatures');
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(error.message || error.error || `Failed to update creature: ${response.statusText}`);
    }

    const data = await response.json();
    return data.creature;
  }

  /**
   * Get multiple creatures by IDs
   * Fetches from user's creatures and shared pool
   */
  async batchGetCreatures(ids: string[]): Promise<BatchCreaturesResponse> {
    const token = await getAuthToken();

    const params = new URLSearchParams({
      ids: ids.join(',')
    });

    const response = await fetch(`${LIBRARY_API}/batch?${params}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(error.error || `Failed to batch get creatures: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Delete a creature and all its associated assets
   * Requires ownership of the creature
   * Returns success status and whether assets were cleaned up
   */
  async deleteCreature(id: string): Promise<{ success: boolean; deletedId: string; assetsDeleted: boolean }> {
    const token = await getAuthToken();

    const response = await fetch(`${LIBRARY_API}/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (response.status === 404) {
      throw new Error('Creature not found');
    }

    if (response.status === 403) {
      throw new Error('You can only delete your own creatures');
    }

    if (response.status === 409) {
      const error = await response.json().catch(() => ({ message: 'Creature is in an active battle' }));
      throw new Error(error.message || 'Cannot delete creature that is currently in an active battle');
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Unknown error' }));
      throw new Error(error.message || `Failed to delete creature: ${response.statusText}`);
    }

    return response.json();
  }
}

// Singleton instance
let serviceInstance: CreatureLibraryService | null = null;

export function getCreatureLibraryService(): CreatureLibraryService {
  if (!serviceInstance) {
    serviceInstance = new CreatureLibraryService();
  }
  return serviceInstance;
}

// Default export
export default getCreatureLibraryService;
